import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * The part of the window a phone's keyboard leaves, while the keyboard is up on the chat.
 *
 * Build round 5 (2026-09-27), from Mattej's iPhone. The chat's shell is `100dvh`, and Safari does not
 * shrink the layout viewport when the keyboard opens: it shrinks the visual viewport and pans the
 * page up under it so the focused field shows. So the whole page slid up, the header left the top of
 * the screen, and the composer ended up wherever the pan put it rather than directly above the
 * keys. Chrome on Android has done the same since version 108 unless a page asks otherwise.
 *
 * `window.visualViewport` is the one measurement of what is actually visible. While the keyboard is
 * up, the shell takes the visual viewport's height and is moved down by its offset, so it covers
 * exactly the visible part of the screen and the composer, its last row, sits on top of the keys.
 * The bottom bar is hidden meanwhile, as a phone's messaging app hides its tabs.
 *
 * **What counts as the keyboard.** The visual viewport more than 120 px shorter than the layout
 * viewport, at a zoom of 1. Pinch zoom also shrinks the visual viewport, and treating that as a
 * keyboard would resize the shell under a person who is only zooming in, so a scale away from 1 is
 * never the keyboard. The 120 px is well above the height Safari's toolbar changes by when it
 * collapses, and well below the shortest keyboard. On a computer the two viewports are the same
 * height and nothing here ever applies.
 *
 * The values are written as custom properties on the shell through the CSSOM, which the portal's
 * content security policy allows, unlike a `style` attribute in markup.
 */
const KEYBOARD_MIN_PX = 120;

export function useKeyboardViewport(active: boolean, shell: RefObject<HTMLElement | null>): boolean {
  const [open, setOpen] = useState(false);
  const frame = useRef(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    const element = shell.current;

    // Nothing to follow: the answer below is false while inactive, whatever was last measured.
    if (!active || !viewport || !element) {
      return;
    }

    const measure = () => {
      window.cancelAnimationFrame(frame.current);
      frame.current = window.requestAnimationFrame(() => {
        const keyboard =
          Math.abs(viewport.scale - 1) < 0.01 &&
          window.innerHeight - viewport.height > KEYBOARD_MIN_PX;

        if (keyboard) {
          element.style.setProperty("--keyboard-viewport-height", `${Math.round(viewport.height)}px`);
          element.style.setProperty("--keyboard-viewport-top", `${Math.round(viewport.offsetTop)}px`);
        } else {
          element.style.removeProperty("--keyboard-viewport-height");
          element.style.removeProperty("--keyboard-viewport-top");
        }

        setOpen(keyboard);
      });
    };

    measure();
    viewport.addEventListener("resize", measure);
    viewport.addEventListener("scroll", measure);

    return () => {
      window.cancelAnimationFrame(frame.current);
      viewport.removeEventListener("resize", measure);
      viewport.removeEventListener("scroll", measure);
      element.style.removeProperty("--keyboard-viewport-height");
      element.style.removeProperty("--keyboard-viewport-top");
      setOpen(false);
    };
  }, [active, shell]);

  return active && open;
}
